import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import DataTab from "@/components/admin/DataTab";
import ResearchAdminTab from "@/components/admin/ResearchAdminTab";
import AudioLibraryTab from "@/components/admin/AudioLibraryTab";
import QuestionnaireAvailabilityTab from "@/components/admin/QuestionnaireAvailabilityTab";
import { CreateClinicModal, EditClinicModal, CreateAdminModal, ClinicStatsModal } from "@/components/admin/modals/SuperAdminModals";
import ProQuestionnaires from "@/components/admin/ProQuestionnaires";
import SecurityProofsTab from "@/components/admin/SecurityProofsTab";
import SystemHealthTab from "@/components/admin/SystemHealthTab";
import BillingTab from "@/components/admin/BillingTab";
import api from "@/api";
import { SectionTabs } from "@/components/shared/SectionTabs";
import { KpiCard, KpiGrid } from "@/components/shared/KpiCard";
import { toast } from "sonner";
import {
  Building2, Users, Activity, BarChart3, Plus,
  RefreshCw, Power, PowerOff, ChevronRight, Search, Eye, Edit2,
  TrendingUp, AlertTriangle, UserPlus,
  Music, Database, FlaskConical, MessageSquare, Volume2, Shield, ShieldCheck, Trash2, ClipboardList, Receipt
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateClinic, setShowCreateClinic] = useState(false);
  const [showEditClinic, setShowEditClinic] = useState(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(null);
  const [showClinicStats, setShowClinicStats] = useState(null);
  
  // Filters
  const [clinicFilter, setClinicFilter] = useState({ search: "", showInactive: false });
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [resetSummary, setResetSummary] = useState(null);
  const [resetForm, setResetForm] = useState({ confirmEmail: "", confirmationText: "", deleteFiles: true });
  const [resetLoading, setResetLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [kpiRes, clinicsRes, adminsRes] = await Promise.all([
        api.get("/kpi/platform").catch(() => ({ data: {} })),
        api.get(`/clinics?includeInactive=${clinicFilter.showInactive}`).catch(() => ({ data: [] })),
        api.get(`/users?role=ADMIN_CLINIQUE&includeInactive=${showInactiveUsers}`).catch(() => ({ data: [] })),
      ]);
      setKpi(kpiRes.data);
      setClinics(clinicsRes.data);
      setAdmins(adminsRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clinicFilter.showInactive, showInactiveUsers]);

  useEffect(() => {
    if (activeTab === "maintenance") loadResetSummary();
  }, [activeTab]);

  const loadResetSummary = async () => {
    try {
      const res = await api.get("/maintenance/production-reset/summary");
      setResetSummary(res.data);
    } catch {
      toast.error("Impossible de charger le résumé maintenance");
    }
  };

  const executeProductionReset = async () => {
    const ok = window.confirm("CONFIRMATION FINALE : supprimer toutes les données de production sauf contact@kiaad.ma ?");
    if (!ok) return;
    setResetLoading(true);
    try {
      const res = await api.post("/maintenance/production-reset/execute", resetForm);
      toast.success("Reset production exécuté");
      setResetForm({ confirmEmail: "", confirmationText: "", deleteFiles: true });
      setResetSummary(null);
      await loadResetSummary();
      console.info("Production reset result", res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset refusé");
    } finally {
      setResetLoading(false);
    }
  };

  // Filter clinics
  const filteredClinics = clinics.filter(c => {
    if (clinicFilter.search) {
      const search = clinicFilter.search.toLowerCase();
      if (!c.name?.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const handleToggleClinicStatus = async (clinic) => {
    const action = clinic.isActive ? "deactivate" : "activate";
    const confirmMsg = clinic.isActive 
      ? `Désactiver la clinique "${clinic.name}"?` 
      : `Réactiver la clinique "${clinic.name}"?`;
    
    if (!confirm(confirmMsg)) return;
    
    try {
      await api.patch(`/clinics/${clinic.id}/${action}`);
      toast.success(clinic.isActive ? "Clinique désactivée" : "Clinique activée");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleToggleUserStatus = async (user) => {
    const action = user.isActive ? "deactivate" : "restore";
    const confirmMsg = user.isActive 
      ? `Suspendre l'utilisateur "${user.firstName} ${user.lastName}"?` 
      : `Réactiver l'utilisateur "${user.firstName} ${user.lastName}"?`;
    
    if (!confirm(confirmMsg)) return;
    
    try {
      if (user.isActive) {
        await api.delete(`/users/${user.id}`);
      } else {
        await api.patch(`/users/${user.id}/restore`);
      }
      toast.success(user.isActive ? "Utilisateur suspendu" : "Utilisateur réactivé");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const viewClinicStats = async (clinic) => {
    try {
      const res = await api.get(`/clinics/${clinic.id}/stats`);
      setShowClinicStats({ ...clinic, stats: res.data });
    } catch (err) {
      toast.error("Erreur lors du chargement des statistiques");
    }
  };

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-slate-500">Gestion de la plateforme HANA</p>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* KPIs */}
        <KpiGrid>
          <KpiCard label="Cliniques actives" value={kpi?.clinics} icon={Building2} testId="kpi-clinics" />
          <KpiCard label="Total patients" value={kpi?.patients?.total} icon={Users} testId="kpi-patients" />
          <KpiCard label="Check-ins / mois" value={kpi?.checkins?.lastMonth} icon={Activity} tone="info" testId="kpi-checkins" />
          <KpiCard label="Taux résolution" value={kpi?.alerts?.resolutionRate ? `${kpi.alerts.resolutionRate}%` : "—"} icon={TrendingUp} tone="warning" testId="kpi-resolution" />
        </KpiGrid>

        {/* Tabs */}
        <SectionTabs
          active={activeTab}
          onChange={(id) => (id === "communication" ? navigate("/superadmin/communication") : setActiveTab(id))}
          tabs={[
            { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
            { id: "clinics", label: "Cliniques", icon: Building2, count: clinics.length },
            { id: "admins", label: "Admins", icon: Users, count: admins.length },
            { id: "billing", label: "Facturation", icon: Receipt },
            { id: "communication", label: "Communications", icon: MessageSquare },
            { id: "data", label: "DATA", icon: Database },
            { id: "questionnaires", label: "Questionnaires", icon: ClipboardList },
            { id: "research", label: "Recherche", icon: FlaskConical },
            { id: "audio", label: "Bibliothèque Audio", icon: Volume2 },
            { id: "ops", label: "Sécurité & Ops", icon: Shield },
            { id: "system", label: "État du système", icon: ShieldCheck },
            { id: "maintenance", label: "Maintenance", icon: Trash2 },
          ]}
        />

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Alerts Summary */}
            <div className="dash-card">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Alertes (30 derniers jours)
              </h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xl font-bold text-slate-700">{kpi?.alerts?.lastMonth ?? 0}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl">
                  <p className="text-xl font-bold text-emerald-600">{kpi?.alerts?.resolved ?? 0}</p>
                  <p className="text-xs text-slate-500">Résolues</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl">
                  <p className="text-xl font-bold text-amber-600">{kpi?.alerts?.resolutionRate ?? 0}%</p>
                  <p className="text-xs text-slate-500">Taux</p>
                </div>
              </div>
            </div>

            {/* Patient Distribution */}
            <div className="dash-card">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                Répartition patients
              </h3>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4 bg-emerald-50 rounded-xl">
                  <p className="text-2xl font-bold text-emerald-600">{kpi?.patients?.active15Plus ?? 0}</p>
                  <p className="text-sm text-slate-500">Actifs +15 jours</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl">
                  <p className="text-2xl font-bold text-purple-600">{kpi?.patients?.active15Minus ?? 0}</p>
                  <p className="text-sm text-slate-500">Nouveaux -15 jours</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="dash-card md:col-span-2">
              <h3 className="font-semibold text-slate-800 mb-4">Actions rapides</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowCreateClinic(true)}
                  data-testid="quick-add-clinic"
                  className="flex items-center gap-2 px-4 py-3 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Nouvelle clinique</span>
                </button>
                <button
                  onClick={() => setActiveTab("clinics")}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">Voir les cliniques</span>
                </button>
                <button
                  onClick={() => navigate("/superadmin/analytics")}
                  data-testid="quick-analytics"
                  className="flex items-center gap-2 px-4 py-3 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 transition-colors"
                >
                  <BarChart3 className="w-5 h-5" />
                  <span className="font-medium">Analytics & Cohortes</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clinics Tab */}
        {activeTab === "clinics" && (
          <div className="space-y-4">
            {/* Actions & Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Rechercher une clinique..."
                    value={clinicFilter.search}
                    onChange={e => setClinicFilter(f => ({ ...f, search: e.target.value }))}
                    className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={clinicFilter.showInactive}
                    onChange={e => setClinicFilter(f => ({ ...f, showInactive: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  Afficher inactives
                </label>
              </div>
              <button
                onClick={() => setShowCreateClinic(true)}
                data-testid="add-clinic-btn"
                className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nouvelle clinique
              </button>
            </div>

            {/* Clinics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClinics.map(clinic => {
                const admin = admins.find(a => a.clinicId === clinic.id);
                return (
                  <div
                    key={clinic.id}
                    data-testid={`clinic-card-${clinic.id}`}
                    className={`dash-card relative ${!clinic.isActive ? "opacity-60 bg-slate-50" : ""}`}
                  >
                    {/* Status Badge */}
                    <div className="absolute top-4 right-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        clinic.isActive 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-red-100 text-red-700"
                      }`}>
                        {clinic.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* Clinic Info */}
                    <div className="mb-4">
                      <h3 className="font-semibold text-slate-800 text-lg pr-16">{clinic.name}</h3>
                      {clinic.address && (
                        <p className="text-sm text-slate-500 mt-1">{clinic.address}</p>
                      )}
                      {clinic.phone && (
                        <p className="text-sm text-slate-500">{clinic.phone}</p>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-slate-700">{clinic.patientCount ?? 0}</p>
                        <p className="text-xs text-slate-500">Patients</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-slate-700">{clinic.userCount ?? 0}</p>
                        <p className="text-xs text-slate-500">Utilisateurs</p>
                      </div>
                    </div>

                    {/* Admin */}
                    <div className="text-sm text-slate-600 mb-4 p-2 bg-slate-50 rounded-lg">
                      {admin ? (
                        <p className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          Admin: <span className="font-medium">{admin.firstName} {admin.lastName}</span>
                        </p>
                      ) : (
                        <p className="text-slate-400 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Pas d'admin assigné
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewClinicStats(clinic)}
                        data-testid={`view-stats-${clinic.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Stats
                      </button>
                      <button
                        onClick={() => setShowEditClinic(clinic)}
                        data-testid={`edit-clinic-${clinic.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Modifier
                      </button>
                      <button
                        onClick={() => handleToggleClinicStatus(clinic)}
                        data-testid={`toggle-clinic-${clinic.id}`}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm rounded-lg transition-colors ${
                          clinic.isActive 
                            ? "text-red-600 hover:bg-red-50" 
                            : "text-emerald-600 hover:bg-emerald-50"
                        }`}
                      >
                        {clinic.isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        {clinic.isActive ? "Désactiver" : "Activer"}
                      </button>
                    </div>

                    {/* Create Admin Button */}
                    {!admin && clinic.isActive && (
                      <button
                        onClick={() => setShowCreateAdmin(clinic)}
                        data-testid={`create-admin-${clinic.id}`}
                        className="w-full mt-3 flex items-center justify-center gap-2 py-2 text-sm bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
                      >
                        <UserPlus className="w-4 h-4" />
                        Créer un admin
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredClinics.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Aucune clinique trouvée</p>
              </div>
            )}
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === "admins" && (
          <div className="space-y-4">
            {/* Filter for inactive users */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showInactiveUsers}
                  onChange={e => setShowInactiveUsers(e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                Afficher les utilisateurs suspendus
              </label>
            </div>

            <div className="dash-card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Admin</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Email</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Clinique</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Statut</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {admins.map(admin => {
                      const clinic = clinics.find(c => c.id === admin.clinicId);
                      return (
                        <tr key={admin.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold flex-shrink-0">
                                {admin.firstName?.[0]}{admin.lastName?.[0]}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-slate-800 whitespace-nowrap">{admin.firstName} {admin.lastName}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-600 whitespace-nowrap">{admin.email}</td>
                          <td className="p-4 text-sm text-slate-600 whitespace-nowrap">{clinic?.name || "—"}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${
                              admin.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}>
                              {admin.isActive ? "Actif" : "Suspendu"}
                            </span>
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => handleToggleUserStatus(admin)}
                              data-testid={`toggle-user-${admin.id}`}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                                admin.isActive 
                                  ? "bg-red-50 text-red-600 hover:bg-red-100" 
                                  : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              }`}
                            >
                              {admin.isActive ? "Suspendre" : "Réactiver"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {admins.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>Aucun admin clinique</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* DATA Tab */}
        {activeTab === "data" && (
          <DataTab />
        )}

        {/* Questionnaires Tab */}
        {activeTab === "questionnaires" && (
          <QuestionnaireAvailabilityTab />
        )}

        {/* Research Admin Tab */}
        {activeTab === "research" && (
          <ResearchAdminTab />
        )}

        {/* Audio Library Tab */}
        {activeTab === "audio" && (
          <AudioLibraryTab />
        )}

        {/* Billing Tab */}
        {activeTab === "billing" && (
          <BillingTab />
        )}

        {/* Security & Ops Tab */}
        {activeTab === "ops" && (
          <SecurityProofsTab />
        )}

        {/* System Health Tab */}
        {activeTab === "system" && (
          <SystemHealthTab />
        )}


        {/* Maintenance Tab */}
        {activeTab === "maintenance" && (
          <div className="space-y-4" data-testid="production-reset-panel">
            <div className="dash-card border-2 border-red-200 bg-red-50/60">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-red-100 text-red-700 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-red-900">Reset production irréversible</h2>
                  <p className="text-sm text-red-800 mt-1">
                    Supprime toutes les cliniques, patients, check-ins, alertes, documents, messages, RWE, bilans et comptes utilisateurs.
                    Le seul compte conservé est <strong>contact@kiaad.ma</strong> en Super Admin SaaS.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="dash-card">
                <p className="text-sm text-slate-500">Utilisateurs supprimables</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="reset-deletable-users-count">{resetSummary?.deletableUsers ?? "—"}</p>
              </div>
              <div className="dash-card">
                <p className="text-sm text-slate-500">Documents DB à supprimer</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="reset-total-documents-count">{resetSummary?.totalDocumentsToDelete ?? "—"}</p>
              </div>
              <div className="dash-card">
                <p className="text-sm text-slate-500">Compte conservé</p>
                <p className="text-base font-bold text-emerald-700" data-testid="reset-kept-email">{resetSummary?.keepEmail || "contact@kiaad.ma"}</p>
                <p className="text-xs text-slate-500">{resetSummary?.superAdminPresent ? "Présent" : "Absent"} — {resetSummary?.superAdminRole || "—"}</p>
              </div>
            </div>

            <div className="dash-card space-y-4 border border-red-200">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email à conserver</label>
                <input
                  value={resetForm.confirmEmail}
                  onChange={(e) => setResetForm(f => ({ ...f, confirmEmail: e.target.value }))}
                  data-testid="reset-confirm-email-input"
                  className="w-full px-3 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-red-200 outline-none"
                  placeholder="contact@kiaad.ma"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Texte de confirmation exact</label>
                <input
                  value={resetForm.confirmationText}
                  onChange={(e) => setResetForm(f => ({ ...f, confirmationText: e.target.value }))}
                  data-testid="reset-confirmation-text-input"
                  className="w-full px-3 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-red-200 outline-none"
                  placeholder={resetSummary?.confirmationText || "EFFACER PRODUCTION HANA"}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={resetForm.deleteFiles}
                  onChange={(e) => setResetForm(f => ({ ...f, deleteFiles: e.target.checked }))}
                  data-testid="reset-delete-files-checkbox"
                  className="w-4 h-4 rounded"
                />
                Supprimer aussi les fichiers uploadés locaux
              </label>
              <button
                onClick={executeProductionReset}
                disabled={resetLoading || resetForm.confirmEmail !== "contact@kiaad.ma" || resetForm.confirmationText !== "EFFACER PRODUCTION HANA"}
                data-testid="execute-production-reset-button"
                className="w-full md:w-auto px-5 py-3 rounded-xl bg-red-600 text-white font-bold disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
              >
                {resetLoading ? "Suppression en cours..." : "Exécuter le reset irréversible"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Clinic Modal */}
      {showCreateClinic && (
        <CreateClinicModal
          onClose={() => setShowCreateClinic(false)}
          onCreated={() => { setShowCreateClinic(false); fetchData(); }}
        />
      )}

      {/* PRO Questionnaires */}
      <div className="mt-8 space-y-6">
        <ProQuestionnaires />
      </div>

      {/* Edit Clinic Modal */}
      {showEditClinic && (
        <EditClinicModal
          clinic={showEditClinic}
          onClose={() => setShowEditClinic(null)}
          onUpdated={() => { setShowEditClinic(null); fetchData(); }}
        />
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <CreateAdminModal
          clinic={showCreateAdmin}
          onClose={() => setShowCreateAdmin(null)}
          onCreated={() => { setShowCreateAdmin(null); fetchData(); }}
        />
      )}

      {/* Clinic Stats Modal */}
      {showClinicStats && (
        <ClinicStatsModal
          clinic={showClinicStats}
          onClose={() => setShowClinicStats(null)}
        />
      )}
    </ProfessionalLayout>
  );
}
